import { SharedAngular } from '@Shared.FormGen/@types/sharedAngular';
import angular from 'angular';

/**
 * @ngdoc directive
 * @name fgImageSelector
 * @description Displays an image field selector for choosing between available business images.
 * @usage
 * ```
 *   <fg-image-selector field="field"></fg-image-selector>
 * ```
 */
angular.module('fg').component('fgImageSelector', {
  bindings: {
    field: '<'
  },
  controller: [
    '$scope',
    'fileService',
    'pubsubService',
    'dialogService',
    'permissionsService',
    function (
      $scope,
      fileService: SharedAngular.FileService,
      pubsubService,
      dialogService,
      permissionsService
    ) {
      const $ctrl = this;
      const SUBSCRIBER_NAME = 'ImageSelector_' + Date.now();

      this.$onInit = function () {
        if ($ctrl.field.alignment === undefined) {
          $ctrl.field.alignment = 'center';
        }
        loadImageIds(true);
        pubsubService.subscribe(
          'IMAGEUPLOAD_UPLOAD_COMPLETED',
          (eventName, data) => loadImageIds(data.fieldId !== $ctrl.field.id),
          SUBSCRIBER_NAME
        );

        $ctrl.canDeleteLogo =
          permissionsService.currentUserHasPermission('logo.delete');
      };

      this.$onDestroy = function () {
        pubsubService.unsubscribeAll(SUBSCRIBER_NAME);
      };

      this.removeLogo = function () {
        dialogService
          .showDialog({
            disableAnimation: true,
            className: 'ngdialog-theme-plain',
            showClose: false,
            template:
              'Client/modeler.workflow/dialogs/dialogs.delete.confirm/dialogs.delete.confirm.tmpl.html',
            controller: 'deleteConfirmDialogController',
            controllerAs: 'ctrl',
            data: {
              entityType: 'Logo',
              entityName: $ctrl.selectedImage.name,
              warnings: [
                'Deleting a logo means it will no longer be available to be selected by anyone for any Flow Model, but will not affect existing Steps that are using it.'
              ],
              lowerCaseDeletePhrase: 'delete',
              showCannotBeUndone: false
            }
          })
          .then((data) => {
            if (dialogService.isCloseModalWithCancelAction(data)) {
              return;
            }
            fileService.deleteLogo($ctrl.field.value).then(() => {
              $ctrl.images.splice($ctrl.currentImageIndex, 1);
              this.changeSelection(0);
            });
          });
      };

      this.changeSelection = function (change) {
        const images = $ctrl.images;
        let newIndex = $ctrl.currentImageIndex + change;
        if (newIndex < 0) {
          newIndex = images.length - 1;
        } else if (newIndex >= images.length) {
          newIndex = 0;
        }
        $ctrl.currentImageIndex = newIndex;
        loadCurrentImageData();
      };

      function loadImageIds(keepSelection) {
        fileService.getBusinessImageIds().then((ids) => {
          $ctrl.errorMessage = null;
          $ctrl.images = null;

          if (ids === null) {
            $ctrl.errorMessage =
              'An error occurred while trying to load existing files, try re-opening the form designer.';
            return;
          }

          $ctrl.images = ids.map((id, index) => {
            return { id: id };
          });

          const currentSelectionIndex = ids.indexOf($ctrl.field.value);
          $ctrl.currentImageIndex =
            keepSelection && currentSelectionIndex > -1
              ? currentSelectionIndex
              : 0;
          loadCurrentImageData();
        });
      }

      function loadCurrentImageData() {
        const image = $ctrl.images[$ctrl.currentImageIndex];
        if (image === undefined) {
          return;
        }
        if (image.src !== undefined) {
          $ctrl.selectedImage = image;
          $ctrl.field.value = image.id;
          return;
        }

        fileService.getFileById(image.id).then((response) => {
          if (response.data) {
            const reader = new FileReader();
            reader.onload = () => {
              image.src = reader.result as string;
              image.name = response.name;
              $ctrl.selectedImage = image;
              $ctrl.field.value = image.id;
              $scope.$apply();
            };
            reader.readAsDataURL(response.data as unknown as Blob);
          } else {
            $ctrl.selectedImage = image;
            $ctrl.field.value = image.id;
          }
        });
      }
    }
  ],
  templateUrl: 'angular-form-gen/form/files/image.selector.ng.html'
});
